import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import { cloneDeep } from 'lodash';
import { BehaviorSubject, Subscription } from 'rxjs';

import { SocialSearchRequestModel } from '../../../api/mentions/model/social-search-request.model';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { MediaSubscription } from '../../../Monitor/Shared/models/media-subscription.model';
import { mediaSubTypeToMediaTypeMap } from '@api/mediaSubscriptions/enums/media-subscription-media-type.enum';
import { MediaTypeModel } from '../../../Monitor/Shared/models/media-type.model';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { SocialRoutingService } from '../../Core/services/social-routing.service';
import { SocialSearchService } from '../../Core/services/social-search.service';
import { SocialRouteTypeEnum } from '../../SocialRouter/enums/social-route-type.enum';
import { SocialRouteModel } from '../../SocialRouter/models/social-route.model';
import { SocialSeriesService } from './social-series.service';
import { mediaSubscriptionMediaTypeEnumToSocialMediaTypeApiEnumMap } from '../../../api/mediaSubscriptions/enums/media-subscription-media-type.enum';


@Injectable()
export class SocialArchiveSearchService implements OnDestroy {

    isArchiveSearch: boolean = false;
    activeArchiveSearch: MediaSubscription;
    activeArchiveSearch$: BehaviorSubject<MediaSubscription> = new BehaviorSubject<MediaSubscription>(null);
    searchDateRange$: BehaviorSubject<DateQueryModel> = new BehaviorSubject<DateQueryModel>(null);
    enableEngagementInsights$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    private socialRouteModel: SocialRouteModel;
    private subscriptions: Subscription[] = [];

    constructor(
        private socialSearchService: SocialSearchService,
        private socialSeriesService: SocialSeriesService,
        private router: Router,
        private socialRoutingService: SocialRoutingService
    ) {
        this.subscriptions.push(
            this.socialRoutingService.getCurrentRoute$().subscribe(mode => {
                this.socialRouteModel = mode;
                // navigate to manage themes page if no archive search is set up
                if ((this.socialRouteModel.type === SocialRouteTypeEnum.archivePostFeed
                    || this.socialRouteModel.type === SocialRouteTypeEnum.archiveAnalytics)
                    && !this.activeArchiveSearch) {
                    this.router.navigate(['Social/themes']);
                }
            })
        );
    }

    archiveSearch(agent: MediaSubscription, searchDateRange: DateQueryModel): void {
        // we need to clear the old filters that were set on a peviously viewed theme
        this.socialSearchService.clearAllFilters();
        this.socialSeriesService.clearAllFilters();

        this.socialRoutingService.isArchiveSearch$.next(true);
        this.activeArchiveSearch = cloneDeep(agent);
        this.activeArchiveSearch$.next(this.activeArchiveSearch);
        this.setSearchDateRange(searchDateRange);

        const mentionSearchCriteriaModel = new MentionSearchCriteriaModel();
        mentionSearchCriteriaModel.query.socialSearchRequest = new SocialSearchRequestModel();

        mentionSearchCriteriaModel.query.socialSearchRequest.anyTerms = [...this.activeArchiveSearch.keywords.or];
        mentionSearchCriteriaModel.query.socialSearchRequest.mustAllTerms = [...this.activeArchiveSearch.keywords.and];
        mentionSearchCriteriaModel.query.socialSearchRequest.mustNotTerms = [...this.activeArchiveSearch.keywords.not];
        mentionSearchCriteriaModel.query.socialSearchRequest.mediaTypeRequests = [...this.activeArchiveSearch.mediaTypeRequests.map(request => {
            return {
                mediaType: mediaSubscriptionMediaTypeEnumToSocialMediaTypeApiEnumMap.get(request.mediaType),
                handles: MediaSubscription.getCleanedHandles(request.mediaType, request.handles)
            };
        })];

        this.activeArchiveSearch.mediaTypes.forEach(type => {
            mentionSearchCriteriaModel.query.includedMediaTypes.push(
                new MediaTypeModel(mediaSubTypeToMediaTypeMap.get(type)));
        });

        this.socialSeriesService.setSearchCriteria(mentionSearchCriteriaModel);
        this.socialSeriesService.clearAgents();
        this.socialSeriesService.setDateQuery(searchDateRange);
        this.socialSearchService.setSearchCriteria(mentionSearchCriteriaModel);
        this.socialSearchService.clearAgents();
        this.socialSearchService.setDateQuery(searchDateRange);
        this.setenableEngagementInsights(true);

        if (this.socialRouteModel?.type === SocialRouteTypeEnum.archivePostFeed) {
            this.socialSearchService.search(true, true);
        } else if (this.socialRouteModel?.type === SocialRouteTypeEnum.archiveAnalytics) {
            this.socialSeriesService.doSearch(true);
        } else if (this.socialRouteModel?.type === SocialRouteTypeEnum.postFeed) {
            this.router.navigate(['Social/archive/results/postFeed']);
        } else {
            this.router.navigate(['Social/archive/results/analytics']);
        }
    }

    setSearchDateRange(searchDateRange: DateQueryModel): void {
        this.searchDateRange$.next(searchDateRange);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    setenableEngagementInsights(enableEngagementInsights: boolean): void {
        this.socialSearchService.enableEngagementInsights(enableEngagementInsights);
        this.socialSearchService.enableEngagementInsights(enableEngagementInsights);
        this.enableEngagementInsights$.next(enableEngagementInsights);
    }
}
